import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from '../../../icons';
import { tShared } from '../../../lib/shared-i18n';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, errorInfo);
    }
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
      return (
        <div className="error-boundary" role="alert">
          <AlertTriangle size={32} />
          <h2>{tShared('shared.error_boundary.heading')}</h2>
          <p>{this.state.error.message}</p>
          <button type="button" onClick={this.reset}>
            {tShared('shared.error_boundary.btn_retry')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
